import { useState } from 'react';
import { format, subDays, subMonths, startOfMonth, endOfMonth, startOfYear } from 'date-fns';
import { Calendar as CalendarIcon, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export interface DateRange {
  from: Date | undefined;
  to: Date | undefined;
}

interface DateRangeFilterProps {
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
  className?: string;
}

type PresetKey = 'last7days' | 'last30days' | 'last90days' | 'thisMonth' | 'lastMonth' | 'thisYear' | 'allTime' | 'custom';

const presets: { key: PresetKey; label: string; getRange: () => DateRange }[] = [
  {
    key: 'last7days',
    label: 'Last 7 days',
    getRange: () => ({ from: subDays(new Date(), 7), to: new Date() }),
  },
  {
    key: 'last30days',
    label: 'Last 30 days',
    getRange: () => ({ from: subDays(new Date(), 30), to: new Date() }),
  },
  {
    key: 'last90days',
    label: 'Last 90 days',
    getRange: () => ({ from: subDays(new Date(), 90), to: new Date() }),
  },
  {
    key: 'thisMonth',
    label: 'This month',
    getRange: () => ({ from: startOfMonth(new Date()), to: new Date() }),
  },
  {
    key: 'lastMonth',
    label: 'Last month',
    getRange: () => {
      const lastMonth = subMonths(new Date(), 1);
      return { from: startOfMonth(lastMonth), to: endOfMonth(lastMonth) };
    },
  },
  {
    key: 'thisYear',
    label: 'This year',
    getRange: () => ({ from: startOfYear(new Date()), to: new Date() }),
  },
  {
    key: 'allTime',
    label: 'All time',
    getRange: () => ({ from: undefined, to: undefined }),
  },
];

export default function DateRangeFilter({
  dateRange,
  onDateRangeChange,
  className,
}: DateRangeFilterProps) {
  const [isCustomOpen, setIsCustomOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState<PresetKey>('allTime');

  const handlePresetSelect = (preset: typeof presets[number]) => {
    setSelectedPreset(preset.key);
    onDateRangeChange(preset.getRange());
  };

  const handleCustomSelect = (range: DateRange) => {
    setSelectedPreset('custom');
    onDateRangeChange(range);
  };

  const getDisplayText = () => {
    if (!dateRange.from && !dateRange.to) {
      return 'All time';
    }
    if (selectedPreset !== 'custom') {
      const preset = presets.find((p) => p.key === selectedPreset);
      if (preset) return preset.label;
    }
    if (dateRange.from && dateRange.to) {
      return `${format(dateRange.from, 'MMM d, yyyy')} - ${format(dateRange.to, 'MMM d, yyyy')}`;
    }
    if (dateRange.from) {
      return `From ${format(dateRange.from, 'MMM d, yyyy')}`;
    }
    return 'Select date range';
  };

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="outline" 
            size="sm" 
            className="h-8 border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
          >
            <CalendarIcon className="mr-2 h-4 w-4 text-gray-500" />
            <span className="text-sm">{getDisplayText()}</span>
            <ChevronDown className="ml-2 h-4 w-4 text-gray-500" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          align="end" 
          className="w-48 bg-white border-gray-200 shadow-lg"
        >
          {presets.map((preset) => (
            <DropdownMenuItem
              key={preset.key}
              onClick={() => handlePresetSelect(preset)}
              className={cn(
                'text-gray-700 hover:bg-gray-100 cursor-pointer',
                selectedPreset === preset.key && 'bg-gray-100 font-medium'
              )}
            >
              {preset.label}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator className="bg-gray-200" />
          <Popover open={isCustomOpen} onOpenChange={setIsCustomOpen}>
            <PopoverTrigger asChild>
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  setIsCustomOpen(true);
                }}
                className={cn(
                  'text-gray-700 hover:bg-gray-100 cursor-pointer',
                  selectedPreset === 'custom' && 'bg-gray-100 font-medium'
                )}
              >
                Custom range...
              </DropdownMenuItem>
            </PopoverTrigger>
            <PopoverContent 
              className="w-auto p-0 bg-white border-gray-200 shadow-lg" 
              align="end"
              side="left"
            >
              <Calendar
                initialFocus
                mode="range"
                defaultMonth={dateRange.from}
                selected={{ from: dateRange.from, to: dateRange.to }}
                onSelect={(range) => {
                  handleCustomSelect({ from: range?.from, to: range?.to });
                  if (range?.from && range?.to) {
                    setIsCustomOpen(false);
                  }
                }}
                numberOfMonths={2}
                className="p-3 pointer-events-auto"
              />
            </PopoverContent>
          </Popover>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
